import Decimal from 'decimal.js';
import React, { useEffect, useRef, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { getCoatingName } from 'src/utils/stowage/tankUtils';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import { roundTo2Decimals, roundTo3Decimals } from 'src/utils/numberHelper';
import { calculateFillRatio, notStowedWeightInTanks, getStowage } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { fillTank } from 'src/utils/stowage/stowageOperation/fillTankUtils';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { getMaxStowedWeight, getMaxUnstowedWeight } from 'src/utils/stowage/stowageOperation/stowageFillUtils';
import { updateUnstowOperationsForCargo } from 'src/utils/stowage/stowageOperation/autoUnstowUtils';
import {
    CARGO_TEMPERATURE_MAX,
    CARGO_TEMPERATURE_MIN,
    getCargoTemperatureFromCommodityBook,
} from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { getAdjustedCargoTemperature, getAdjustedCargoTemperatureInTank } from 'src/utils/stowage/stowageOperation/adjustedCargoTemperatureUtils';
import {
    ElementWrapper,
    PanelButton,
    PanelFlexWrapper,
    PanelSectionContent,
    PanelSubHeader,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from 'src/app/useAppFeature';
import useStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getCargoOperationWeight, getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import { getSwingValues, SwingValues } from 'src/utils/stowage/cargoOptionUtils';
import { updateManifest } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { getCargoContentWeight } from 'src/utils/stowage/stowageOperation/cargoContentUtils';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import {
    calculateContentWeight,
    calculateContentVolume,
    getMaxFillRatio as getMaxFillRatioUtil,
    getActualCargoTemperature as getActualCargoTemperatureUtil,
    getTankDescription as getTankDescriptionUtil,
    getTankVolume as getTankVolumeUtil,
    getCargoTemperatureInTank as getCargoTemperatureInTankUtil,
    FILL_RATIO_98_PCT,
    isStowedWeightValid,
    isStowedVolumeValid,
    isFillRatioValid,
    calculateNotStowedWeightForCargoOperation,
    calculateNotStowedVolumeForCargoOperation,
    getMaxStowageVolume,
    getMaxStowageWeight,
    getTotalNominatedSwingWeight,
    getTotalNominatedSwingVolume,
    getWeightRemainingErrorMessage,
    getVolumeRemainingErrorMessage,
    updateStowedWeight,
    updateStowedVolume,
    updateFillRatio,
    updateCargoTemperature,
    canSaveStowage,
} from 'src/utils/stowage/stowageOperation/fillTankPanelUtils';
import { STOWAGE_INPUT_DECIMAL_PLACES } from 'src/utils/stowage/stowagePrecision';

type StowageVM = {
    tank: Tank;
    cargoContents: CargoContent[];
    stowageItem: Stowage;
    initialContentWeight: Decimal;
    stowedWeight: Decimal;
    stowedVolume: Decimal;
    fillRatio: Decimal;
    overrideFillRatio?: boolean;
    shipFigure?: number | null;
    isShipFigureAdjusted?: boolean;
    cargoTemperatureInTank?: number | null;
    notValidFieldsArray?: [];
    hasInvalidFields?: boolean;
    isLoading?: boolean;
    isCargoTemperatureAdjusted: boolean;
    swingValues: SwingValues;
    cargoNominatedWeight: number | null;
    cargoContractWeight: number | null;
};

type Props = {
    tank: Tank;
    cargoContents: CargoContent[];
    stowagePlanChanges?: StowagePlanChange[];
};

const FillTankPanel: React.FC<Props> = (props) => {
    const { selectedCargoOperation } = useStowageSelectionStore();

    if (!selectedCargoOperation) return <></>;

    const panelContentRef = useRef<HTMLInputElement>(null);
    const { setIsFillPanelOpen, stowagePlans, activeStowagePlan } = useStowageStore();
    const { saveStowageData } = useStowageApi();
    const [stowageVM, setStowageVM] = useState<StowageVM>(null);
    const { isFeatureEnabled } = useAppFeature();
    const { selectedVessel } = useVesselStore();

    useEffect(() => {
        if (!selectedCargoOperation || !activeStowagePlan || !props.tank) {
            return;
        }
        if (selectedCargoOperation.operationType !== 'Loading' && !props.cargoContents) {
            return;
        }

        const stowageItem = getStowage(activeStowagePlan, selectedCargoOperation, props.tank);

        if (stowageItem && props.cargoContents) {
            setInitialStowageVmFromStowage(props.tank, props.cargoContents, stowageItem);
        } else {
            setInitialStowageVmToDefault(props.tank, props.cargoContents);
        }
    }, []);

    const setInitialStowageVmFromStowage = (tank: Tank, cargoContents: CargoContent[], stowageItem: Stowage) => {
        const isLoading = selectedCargoOperation.operationType === 'Loading';
        const cargoContentWeight = getCargoContentWeight(cargoContents);
        const initialContentWeight = cargoContentWeight.minus(stowageItem.weight ?? new Decimal(0));
        // For unloading operations, stowageItem.weight is already negative, so take absolute value for UI display
        // For loading operations, use the weight as-is (positive)
        const stowedWeight = isLoading ? new Decimal(stowageItem.weight) : new Decimal(stowageItem.weight).abs();

        const cargoTemperatureInTank =
            getAdjustedCargoTemperatureInTank(activeStowagePlan, stowageItem.cargoOperation.cargo, tank)?.temperature ?? null;
        const actualCargoTemperature =
            getAdjustedCargoTemperature(activeStowagePlan, selectedCargoOperation.cargo)?.temperature ??
            getCargoTemperatureFromCommodityBook(selectedCargoOperation.cargo);
        const stowedVolume = calculateVolume(stowedWeight, stowageItem.cargoOperation.cargo, actualCargoTemperature);
        const currentContentVolume = calculateVolume(cargoContentWeight, stowageItem.cargoOperation.cargo, actualCargoTemperature);
        const fillRatio = calculateFillRatio(currentContentVolume, props.tank);

        const initialStowageVM: StowageVM = {
            tank: tank,
            cargoContents: cargoContents,
            stowageItem: stowageItem,
            initialContentWeight: initialContentWeight,
            stowedWeight: stowedWeight,
            stowedVolume: stowedVolume,
            fillRatio: fillRatio,
            shipFigure: stowageItem.shipFigure,
            isShipFigureAdjusted: stowageItem.isShipFigureAdjusted,
            cargoTemperatureInTank: cargoTemperatureInTank,
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isLoading: isLoading,
            isCargoTemperatureAdjusted: cargoTemperatureInTank !== null,
            swingValues: null,
            cargoNominatedWeight: null,
            cargoContractWeight: null,
        };

        initialStowageVM.cargoNominatedWeight = getCargoOperationWeight(activeStowagePlan, stowageItem.cargoOperation.cargo, 'Loading', 'nominated');
        initialStowageVM.cargoContractWeight = getCargoOperationWeight(activeStowagePlan, stowageItem.cargoOperation.cargo, 'Loading', 'contract');
        initialStowageVM.swingValues = getSwingValues(initialStowageVM.cargoContractWeight, stowageItem.cargoOperation.cargo);

        if (initialStowageVM.cargoTemperatureInTank) {
            updateCargoTemperature(initialStowageVM, selectedCargoOperation);
        }

        const initialContentVolume = calculateVolume(initialStowageVM.initialContentWeight, selectedCargoOperation.cargo, actualCargoTemperature);
        const initialFillRatio = calculateFillRatio(initialContentVolume, initialStowageVM.tank);
        initialStowageVM.overrideFillRatio = initialStowageVM.isLoading
            ? initialStowageVM.fillRatio.gt(FILL_RATIO_98_PCT)
            : initialFillRatio.gt(FILL_RATIO_98_PCT);

        setStowageVM(initialStowageVM);
    };

    const setInitialStowageVmToDefault = (tank: Tank, cargoContents: CargoContent[]) => {
        const cargoTemperatureInTank = getAdjustedCargoTemperatureInTank(activeStowagePlan, selectedCargoOperation.cargo, tank)?.temperature ?? null;
        const initialStowageVM: StowageVM = {
            tank: tank,
            cargoContents: cargoContents,
            stowageItem: null,
            initialContentWeight: getCargoContentWeight(cargoContents),
            stowedWeight: new Decimal(0),
            stowedVolume: new Decimal(0),
            fillRatio: new Decimal(0),
            overrideFillRatio: false,
            shipFigure: null,
            isShipFigureAdjusted: false,
            cargoTemperatureInTank: cargoTemperatureInTank,
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isLoading: selectedCargoOperation.operationType === 'Loading',
            isCargoTemperatureAdjusted: cargoTemperatureInTank !== null,
            swingValues: null,
            cargoNominatedWeight: null,
            cargoContractWeight: null,
        };

        initialStowageVM.cargoNominatedWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Loading', 'nominated');
        initialStowageVM.cargoContractWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Loading', 'contract');
        initialStowageVM.swingValues = getSwingValues(initialStowageVM.cargoContractWeight, selectedCargoOperation.cargo);

        if (initialStowageVM.isLoading) {
            stowMaxInTank(initialStowageVM);
        } else {
            unstowMaxFromTank(initialStowageVM);
        }
        setStowageVM(initialStowageVM);
    };
    const stowMaxInTank = (vm: StowageVM) => {
        const actualCargoTemperature = getActualCargoTemperatureUtil(vm, selectedCargoOperation, activeStowagePlan);
        const cargoContentWeight: Decimal = getCargoContentWeight(vm.cargoContents);
        const stowageItemWeight: Decimal = vm.stowageItem?.weight ?? new Decimal(0);
        const notStowedWeight: Decimal = notStowedWeightInTanks(activeStowagePlan, selectedCargoOperation);
        const availableWeight: Decimal = notStowedWeight.add(stowageItemWeight);

        const maxStowedWeight = getMaxStowedWeight(
            selectedCargoOperation.cargo,
            new Decimal(vm.tank.maxWeightAt98Percent),
            new Decimal(vm.overrideFillRatio ? vm.tank.volume : vm.tank.volumeAt98Percent),
            cargoContentWeight.minus(stowageItemWeight),
            availableWeight,
            actualCargoTemperature
        );

        // Round to 3 decimal places to prevent floating-point precision issues
        vm.stowedWeight = maxStowedWeight.toDecimalPlaces(STOWAGE_INPUT_DECIMAL_PLACES);
        vm.stowedVolume = calculateVolume(vm.stowedWeight, selectedCargoOperation.cargo, actualCargoTemperature).toDecimalPlaces(
            STOWAGE_INPUT_DECIMAL_PLACES
        );
        const contentWeight = vm.initialContentWeight.add(vm.stowedWeight);
        const contentVolume = calculateVolume(contentWeight, selectedCargoOperation.cargo, actualCargoTemperature);
        vm.fillRatio = calculateFillRatio(contentVolume, vm.tank);
    };

    const unstowMaxFromTank = (vm: StowageVM) => {
        const actualCargoTemperature = getActualCargoTemperatureUtil(vm, selectedCargoOperation, activeStowagePlan);
        const previousContentWeight = vm.initialContentWeight;
        const stowageItemWeight = vm.stowageItem?.weight ?? new Decimal(0);
        const notStowedWeight = notStowedWeightInTanks(activeStowagePlan, selectedCargoOperation);
        const availableWeight = notStowedWeight.add(stowageItemWeight).times(-1);

        const maxUnstowedWeight = getMaxUnstowedWeight(previousContentWeight, availableWeight);

        vm.stowedWeight = maxUnstowedWeight;
        vm.stowedVolume = calculateVolume(vm.stowedWeight, selectedCargoOperation.cargo, actualCargoTemperature).toDecimalPlaces(
            STOWAGE_INPUT_DECIMAL_PLACES
        );

        const previousContentVolume = calculateVolume(previousContentWeight, selectedCargoOperation.cargo, actualCargoTemperature);
        const initialFillRatio = calculateFillRatio(previousContentVolume, vm.tank);
        vm.overrideFillRatio = initialFillRatio.gt(FILL_RATIO_98_PCT);

        vm.fillRatio = calculateFillRatio(previousContentVolume.minus(vm.stowedVolume), vm.tank);
    };

    const onCancel = () => {
        setIsFillPanelOpen(false);
    };
    const onStowMaxInTank = () => {
        stowMaxInTank(stowageVM);
        validateInputs(stowageVM);
        setStowageVM({ ...stowageVM });
    };

    const onUnstowMaxFromTank = () => {
        unstowMaxFromTank(stowageVM);
        validateInputs(stowageVM);
        setStowageVM({ ...stowageVM });
    };

    const onShowCargoTempAdjust = () => {
        stowageVM.isCargoTemperatureAdjusted = !stowageVM.isCargoTemperatureAdjusted;
        setStowageVM({ ...stowageVM });
    };
    const onSave = async () => {
        if (!stowagePlans || !activeStowagePlan) {
            setIsFillPanelOpen(false);
            return;
        }

        const cargoTemperature = getActualCargoTemperatureUtil(stowageVM, selectedCargoOperation, activeStowagePlan);
        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        // For unloading operations, negate the stowedWeight since it represents removal
        const weightToSave = stowageVM.isLoading ? stowageVM.stowedWeight : stowageVM.stowedWeight.times(-1);
        const fillTankResponse = fillTank(
            _stowagePlan,
            selectedCargoOperation,
            stowageVM.tank,
            weightToSave,
            cargoTemperature,
            stowageVM.isCargoTemperatureAdjusted,
            true
        );

        if (!fillTankResponse?.stowagePlanChanges || fillTankResponse.stowagePlanChanges?.length === 0) {
            setIsFillPanelOpen(false);
            return;
        }

        updateManifest(_stowagePlan, selectedVessel);
        const unstowStowagePlanChanges = updateUnstowOperationsForCargo(_stowagePlan, selectedCargoOperation);
        const propChanges = props.stowagePlanChanges ?? [];
        await saveStowageData(_stowagePlan, fillTankResponse.stowagePlanChanges.concat(unstowStowagePlanChanges).concat(propChanges));

        setIsFillPanelOpen(false);
    };

    const isFieldValid = (vm: StowageVM, field: string): boolean => {
        switch (field) {
            case 'stowedWeight': {
                const isStowedWeightValidResult = isStowedWeightValid(vm.stowedWeight, getMaxStowageWeight(vm, vm.cargoNominatedWeight));
                const isNotStowedWeightValid = calculateNotStowedWeightForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
                const isNotStowedVolumeValid = calculateNotStowedVolumeForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
                return isStowedWeightValidResult && isNotStowedWeightValid && isNotStowedVolumeValid;
            }
            case 'stowedVolume': {
                const isStowedVolumeValidResult = isStowedVolumeValid(vm.stowedVolume, getMaxStowageVolume(vm, selectedCargoOperation));
                const isNotStowedWeightValid = calculateNotStowedWeightForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
                const isNotStowedVolumeValid = calculateNotStowedVolumeForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
                return isStowedVolumeValidResult && isNotStowedWeightValid && isNotStowedVolumeValid;
            }
            case 'fillRatio': {
                return isFillRatioValid(vm.fillRatio, getMaxFillRatioUtil(vm));
            }
            case 'overrideFillRatio': {
                return true;
            }
            case 'notStowedWeight': {
                return calculateNotStowedWeightForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
            }
            case 'notStowedVolume': {
                return calculateNotStowedVolumeForCargoOperation(vm, selectedCargoOperation, activeStowagePlan).gte(0);
            }
            default: {
                return false;
            }
        }
    };

    const checkInvalidOverride = (stowageOperationVM: StowageVM): void => {
        const fillRatioValid = isFillRatioValid(stowageOperationVM.fillRatio, getMaxFillRatioUtil(stowageOperationVM));
        stowageOperationVM.hasInvalidFields = stowageOperationVM.hasInvalidFields || !fillRatioValid;
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const { name, type, checked, value: targetValue } = changeObject.event.target;
        if (type === 'checkbox') stowageVM[name] = checked;
        else if (name === 'fillRatio') stowageVM[name] = new Decimal((targetValue ? +targetValue : 0) / 100);
        else if (name === 'stowedWeight' || name === 'stowedVolume') stowageVM[name] = new Decimal(targetValue ? +targetValue : 0);
        else stowageVM[name] = targetValue ? +targetValue : 0;

        if (name === 'stowedWeight') {
            updateStowedWeight(
                stowageVM,
                selectedCargoOperation,
                getActualCargoTemperatureUtil(stowageVM, selectedCargoOperation, activeStowagePlan)
            );
        } else if (name === 'stowedVolume') {
            updateStowedVolume(
                stowageVM,
                selectedCargoOperation,
                getActualCargoTemperatureUtil(stowageVM, selectedCargoOperation, activeStowagePlan)
            );
        } else if (name === 'fillRatio') {
            updateFillRatio(stowageVM, selectedCargoOperation, getActualCargoTemperatureUtil(stowageVM, selectedCargoOperation, activeStowagePlan));
        } else if (name === 'cargoTemperatureInTank') {
            updateCargoTemperature(stowageVM, selectedCargoOperation);
        }

        validateInputs(stowageVM, name, changeObject.isValid);
        setStowageVM({ ...stowageVM });
    };

    const validateFields = (fields: string[]) => {
        fields.forEach((field) => {
            hasInvalidFields(stowageVM, field, isFieldValid(stowageVM, field));
        });
    };
    const validateInputs = (vm: StowageVM, fieldName?: string, isValid?: boolean) => {
        const stowageOperationFields = ['stowedWeight', 'stowedVolume', 'fillRatio', 'overrideFillRatio', 'notStowedWeight', 'notStowedVolume'];
        if (!fieldName || stowageOperationFields.includes(fieldName)) {
            validateFields(stowageOperationFields);
        } else if (fieldName) {
            hasInvalidFields(vm, fieldName, isValid);
        }
        checkInvalidOverride(vm);
    };
    const canSave = (): boolean => {
        const remainingWeight = calculateNotStowedWeightForCargoOperation(stowageVM, selectedCargoOperation, activeStowagePlan);
        const remainingVolume = calculateNotStowedVolumeForCargoOperation(stowageVM, selectedCargoOperation, activeStowagePlan);
        return canSaveStowage(stowageVM, remainingWeight, remainingVolume);
    };

    return (
        stowageVM?.tank && (
            <OdfjellPanel
                headerTitle={stowageVM.isLoading ? 'Stow cargo' : 'Unstow cargo'}
                extHeaderTitle={
                    getFormattedCargoNumber(
                        selectedCargoOperation.cargo.voyageNumber,
                        selectedCargoOperation.cargo.cargoNumber,
                        isManualCargo(selectedCargoOperation.cargo)
                    ) +
                    ' ' +
                    selectedCargoOperation.cargo.product.tradeName
                }
                defaultPosition="center"
                onCancel={onCancel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={!canSave()}
                preventOutsideClose={true}
                hasCurtain={true}
                leftButtons={
                    <>
                        {stowageVM.isLoading && (
                            <PanelButton disabled={false} isSelected={false} onClick={onStowMaxInTank} title="Stow as much as possible in tank">
                                Fill up tank
                                <FontAwesomeIcon className="icon" icon="upload" />
                            </PanelButton>
                        )}
                        {!stowageVM.isLoading && (
                            <PanelButton
                                disabled={false}
                                isSelected={false}
                                onClick={onUnstowMaxFromTank}
                                title="Unstow as much as possible from tank"
                            >
                                Unstow max
                                <FontAwesomeIcon className="icon" icon="upload" />
                            </PanelButton>
                        )}
                        {isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageCargoTemperaturePerTank, date: new Date() }) && (
                            <PanelButton
                                disabled={false}
                                isSelected={stowageVM.isCargoTemperatureAdjusted}
                                onClick={onShowCargoTempAdjust}
                                title="Adjust cargo temperature"
                            >
                                Adjust temperature
                                <FontAwesomeIcon className="icon" icon="temperature-high" />
                            </PanelButton>
                        )}
                    </>
                }
            >
                <div ref={panelContentRef}>
                    <PanelFlexWrapper>
                        <div className="left-wrapper">
                            <PanelSectionContent>
                                <PanelSubHeader>
                                    Cargo
                                    <span className="ext-info padding-right">total nominated / swing</span>
                                </PanelSubHeader>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    description={'Weight'}
                                    value={getTotalNominatedSwingWeight(stowageVM.cargoNominatedWeight, stowageVM.swingValues)}
                                    name={'totalNominatedWeight'}
                                    onChange={null}
                                    unit={'MT'}
                                />
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    description={'Volume'}
                                    value={getTotalNominatedSwingVolume(
                                        stowageVM.cargoNominatedWeight,
                                        stowageVM.swingValues,
                                        selectedCargoOperation,
                                        getActualCargoTemperatureUtil(stowageVM, selectedCargoOperation, activeStowagePlan)
                                    )}
                                    name={'totalNominatedVolume'}
                                    onChange={null}
                                    unit={'m³'}
                                />
                            </PanelSectionContent>
                            <PanelSectionContent>
                                <PanelSubHeader>Operation</PanelSubHeader>
                                <div className="highlight-panel">
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={false}
                                            type={'number'}
                                            description={stowageVM.isLoading ? 'Stowed weight' : 'Unstowed weight'}
                                            value={roundTo3Decimals(stowageVM.stowedWeight.toNumber()).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                            min={0}
                                            max={roundTo3Decimals(getMaxStowageWeight(stowageVM, stowageVM.cargoNominatedWeight).toNumber())}
                                            name={'stowedWeight'}
                                            onChange={onInputChange}
                                            dataObject={stowageVM}
                                            unit={'MT'}
                                            inputSize="large"
                                            validationInTooltip={true}
                                            tabIndex={1}
                                            key={'input_stowed_weight'}
                                            maxDecimals={STOWAGE_INPUT_DECIMAL_PLACES}
                                            customValidations={[
                                                {
                                                    validationFunction: () =>
                                                        !calculateNotStowedWeightForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ).gte(0),
                                                    errorMessage: getWeightRemainingErrorMessage(
                                                        calculateNotStowedWeightForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ),
                                                        stowageVM.stowedWeight
                                                    ),
                                                },
                                            ]}
                                        />
                                    </ElementWrapper>
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={false}
                                            type={'number'}
                                            description={stowageVM.isLoading ? 'Stowed volume' : 'Unstowed volume'}
                                            value={roundTo3Decimals(stowageVM.stowedVolume.toNumber()).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                            min={0}
                                            name={'stowedVolume'}
                                            max={roundTo3Decimals(getMaxStowageVolume(stowageVM, selectedCargoOperation).toNumber())}
                                            onChange={onInputChange}
                                            dataObject={stowageVM}
                                            unit={'m³'}
                                            inputSize="large"
                                            validationInTooltip={true}
                                            tabIndex={2}
                                            key={'input_stowed_volume'}
                                            maxDecimals={STOWAGE_INPUT_DECIMAL_PLACES}
                                            customValidations={[
                                                {
                                                    validationFunction: () =>
                                                        !calculateNotStowedVolumeForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ).gte(0),
                                                    errorMessage: getVolumeRemainingErrorMessage(
                                                        calculateNotStowedVolumeForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ),
                                                        stowageVM.stowedVolume
                                                    ),
                                                },
                                            ]}
                                        />
                                    </ElementWrapper>
                                </div>
                                {stowageVM.isCargoTemperatureAdjusted && (
                                    <div className="highlight-panel">
                                        <ElementWrapper>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'number'}
                                                description="Product temperature in Commodity Book"
                                                value={getCargoTemperatureFromCommodityBook(selectedCargoOperation.cargo)}
                                                name={'productTemperature'}
                                                onChange={onInputChange}
                                                dataObject={stowageVM}
                                                unit={'°C'}
                                                validationInTooltip={true}
                                                key={'input_product_temperature'}
                                            />
                                        </ElementWrapper>
                                        <ElementWrapper>
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'number'}
                                                description="Cargo temperature"
                                                value={getAdjustedCargoTemperature(activeStowagePlan, selectedCargoOperation.cargo)?.temperature}
                                                name={'cargoTemperature'}
                                                onChange={onInputChange}
                                                dataObject={stowageVM}
                                                unit={'°C'}
                                                validationInTooltip={true}
                                                key={'input_cargo_temperature'}
                                            />
                                        </ElementWrapper>
                                        <ElementWrapper>
                                            <OdfjellInputComponent
                                                isReadOnly={false}
                                                type={'number'}
                                                description="Cargo temperature in tank"
                                                value={getCargoTemperatureInTankUtil(stowageVM)}
                                                min={CARGO_TEMPERATURE_MIN}
                                                max={CARGO_TEMPERATURE_MAX}
                                                name={'cargoTemperatureInTank'}
                                                onChange={onInputChange}
                                                dataObject={stowageVM}
                                                unit={'°C'}
                                                inputSize="small"
                                                validationInTooltip={true}
                                                tabIndex={5}
                                                key={'input_cargo_temperature_in_tank'}
                                            />
                                        </ElementWrapper>
                                    </div>
                                )}
                                <PanelSectionContent>
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={true}
                                            type={'number'}
                                            description={'Weight remaining'}
                                            value={roundTo3Decimals(
                                                calculateNotStowedWeightForCargoOperation(
                                                    stowageVM,
                                                    selectedCargoOperation,
                                                    activeStowagePlan
                                                ).toNumber()
                                            ).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                            name={'weightRemaining'}
                                            onChange={null}
                                            leaveBlank={true}
                                            unit={'MT'}
                                            customValidations={[
                                                {
                                                    validationFunction: () =>
                                                        !calculateNotStowedWeightForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ).gte(0),
                                                    errorMessage: 'Total stowed weight exceeds nominated weight',
                                                },
                                            ]}
                                            validationInTooltip={true}
                                            key={'input_weight_remaining'}
                                        />
                                    </ElementWrapper>
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={true}
                                            type={'number'}
                                            description={'Volume remaining'}
                                            value={roundTo3Decimals(
                                                calculateNotStowedVolumeForCargoOperation(
                                                    stowageVM,
                                                    selectedCargoOperation,
                                                    activeStowagePlan
                                                ).toNumber()
                                            ).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                            name={'volumeRemaining'}
                                            onChange={null}
                                            leaveBlank={true}
                                            unit={'m³'}
                                            key={'input_volume_remaining'}
                                            customValidations={[
                                                {
                                                    validationFunction: () =>
                                                        !calculateNotStowedVolumeForCargoOperation(
                                                            stowageVM,
                                                            selectedCargoOperation,
                                                            activeStowagePlan
                                                        ).gte(0),
                                                    errorMessage: 'Total stowed volume exceeds nominated volume',
                                                },
                                            ]}
                                            validationInTooltip={true}
                                        />
                                    </ElementWrapper>
                                </PanelSectionContent>
                            </PanelSectionContent>
                        </div>
                        <div className="right-wrapper">
                            <PanelSectionContent>
                                <PanelSubHeader>
                                    Tank {stowageVM.tank.odfjellTankName} ({getCoatingName(stowageVM.tank.coatingType)})
                                </PanelSubHeader>
                                <ElementWrapper>
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'number'}
                                        description="Max. weight"
                                        value={stowageVM.tank.maxWeightAt98Percent.toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                        name={'tankMaxWeight'}
                                        onChange={null}
                                        unit={'MT'}
                                        leaveBlank={true}
                                        key={'input_tank_max_weight'}
                                    />
                                </ElementWrapper>
                                <ElementWrapper>
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'number'}
                                        description={getTankDescriptionUtil(stowageVM)}
                                        value={getTankVolumeUtil(stowageVM).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                        name={'tankVolume'}
                                        onChange={null}
                                        unit={'m³'}
                                        leaveBlank={true}
                                        key={'input_tank_volume'}
                                    />
                                </ElementWrapper>
                            </PanelSectionContent>

                            <PanelSectionContent>
                                <PanelSubHeader>Tank content</PanelSubHeader>
                                <div className="highlight-panel">
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={false}
                                            type={'checkbox'}
                                            description={'Override 98%'}
                                            dataObject={stowageVM}
                                            name={'overrideFillRatio'}
                                            value={stowageVM.overrideFillRatio}
                                            validationInTooltip={true}
                                            tabIndex={3}
                                            onChange={onInputChange}
                                        ></OdfjellInputComponent>{' '}
                                        <OdfjellInputComponent
                                            isReadOnly={false}
                                            type={'number'}
                                            description={'Fill ratio'}
                                            value={roundTo2Decimals(stowageVM.fillRatio.toNumber() * 100)}
                                            name={'fillRatio'}
                                            onChange={onInputChange}
                                            dataObject={stowageVM}
                                            leaveBlank={true}
                                            min={0}
                                            max={getMaxFillRatioUtil(stowageVM).toNumber() * 100}
                                            unit={'%'}
                                            inputSize="small"
                                            validationInTooltip={true}
                                            tabIndex={4}
                                            elementId={'fillRatio'}
                                            key={'input_fill_ratio'}
                                        />
                                    </ElementWrapper>
                                </div>
                                <PanelSectionContent>
                                    <ElementWrapper>
                                        <OdfjellInputComponent
                                            isReadOnly={true}
                                            type={'number'}
                                            description={'Content weight'}
                                            value={roundTo3Decimals(calculateContentWeight(stowageVM).toNumber()).toFixed(
                                                STOWAGE_INPUT_DECIMAL_PLACES
                                            )}
                                            name={'contentWeight'}
                                            dataObject={stowageVM}
                                            leaveBlank={true}
                                            unit={'MT'}
                                            key={'input_content_weight'}
                                        />
                                        <OdfjellInputComponent
                                            isReadOnly={true}
                                            type={'number'}
                                            description={'Content volume'}
                                            value={roundTo3Decimals(
                                                calculateContentVolume(stowageVM, selectedCargoOperation, activeStowagePlan).toNumber()
                                            ).toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                            name={'contentVolume'}
                                            leaveBlank={true}
                                            unit={'m³'}
                                            key={'input_content_volume'}
                                        />
                                        {stowageVM.shipFigure && (
                                            <OdfjellInputComponent
                                                isReadOnly={true}
                                                type={'number'}
                                                description={'Ship figure'}
                                                value={stowageVM.shipFigure.toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                                name={'shipFigure'}
                                                dataObject={stowageVM}
                                                leaveBlank={true}
                                                unit={'MT'}
                                                validationInTooltip={true}
                                                key={'input_ship_figure'}
                                            />
                                        )}
                                    </ElementWrapper>
                                </PanelSectionContent>
                            </PanelSectionContent>
                        </div>
                    </PanelFlexWrapper>
                </div>
            </OdfjellPanel>
        )
    );
};

export default FillTankPanel;
